import { AxiosError } from 'axios';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useApi } from 'src/hooks/useApi';

/**
 * Shared data layer for the Recently_Updated_Notifications feature.
 *
 * Backs the Recently_Updated_Bell, the Personal_Updates_Banner and the
 * Recently_Updated_Page. Each hook wraps the project's `useApi` /
 * `createAxiosInstance` fetch helper, which attaches the Cognito ID
 * token via the existing request interceptor — the same mechanism the
 * sibling `/user-profile/*` hooks use.
 *
 * On a fetch failure, each hook resolves to `{ data: undefined, error }`
 * and logs `console.error` with structured request context. Nothing
 * throws out of these hooks (requirement 7.4): a 503 is treated the same
 * as "empty list" by the UI layer.
 */

export type RecentlyUpdatedScope = 'global' | 'personal';

export interface RecentlyUpdatedEntry {
    documentId: number;
    documentTitle: string;
    /** ISO-8601 timestamp of the document's most-recently-changed topic. */
    latestChangeAt: string;
    changedTopicCount: number;
    /** `lcmsXmlGUID` of the newest-changed topic within the document. */
    latestChangedTopicGuid: string;
}

export interface RecentlyUpdatedResponse {
    scope: RecentlyUpdatedScope;
    items: RecentlyUpdatedEntry[];
    nextCursor?: string | null;
}

export interface UseRecentlyUpdatedListResult {
    data?: RecentlyUpdatedEntry[];
    nextCursor?: string | null;
    loading: boolean;
    error?: Error | AxiosError;
    /**
     * Fetches the next page using the current `nextCursor` and concatenates
     * the entries into the hook's local accumulator. No-op when the hook has
     * no cursor (either the initial fetch has not landed yet or the server
     * already indicated the end of the list).
     */
    loadMore: () => Promise<void>;
}

export interface UpdatesReadStateResponse {
    lastViewedAt: number | null;
}

export interface UseUpdatesReadStateResult {
    lastViewedAt: number | null;
    loading: boolean;
    error?: Error | AxiosError;
    /** Fires `POST /me/recently-updated-read-state/mark-read`. */
    markRead: () => Promise<void>;
}

const LIST_ENDPOINT = 'recently-updated-documents';
const READ_STATE_ENDPOINT = 'me/recently-updated-read-state';
const MARK_READ_ENDPOINT = `${READ_STATE_ENDPOINT}/mark-read`;

/**
 * Shared "unread" predicate used by Recently_Updated_Bell and
 * Personal_Updates_Banner. Both surfaces must agree on whether a given
 * entries list is unread for the user, so the comparison lives here and
 * is called in exactly one domain — epoch-ms.
 *
 * Why epoch-ms (and not the ISO-string lexicographic compare the bell
 * previously used): `Date.prototype.toISOString()` always emits
 * millisecond precision (`.000Z`), but backend `latestChangeAt` values
 * may not. For an equal instant, `'2026-10-21T18:55:00Z'` is
 * lexicographically greater than `'2026-10-21T18:55:00.000Z'` because
 * `'Z'` (0x5A) sorts after `'.'` (0x2E), which falsely marks the bell
 * unread while the banner (already on epoch-ms) correctly stays
 * dismissed. Comparing both in epoch-ms keeps the two surfaces in
 * agreement regardless of whether the backend omits milliseconds.
 *
 *  - entries undefined or empty          -> false
 *  - lastViewedAt null, any parseable    -> true
 *  - a non-parseable latestChangeAt      -> skipped (not counted as unread)
 */
export const isUnreadForUser = (
    entries:
        | ReadonlyArray<Pick<RecentlyUpdatedEntry, 'latestChangeAt'>>
        | undefined,
    lastViewedAt: number | null
): boolean => {
    if (!entries || entries.length === 0) {
        return false;
    }
    return entries.some((e) => {
        const changeMs = Date.parse(e.latestChangeAt);
        if (Number.isNaN(changeMs)) {
            return false;
        }
        if (lastViewedAt == null) {
            return true;
        }
        return changeMs > lastViewedAt;
    });
};

/**
 * Fetches a Recently_Updated_Response for the given scope + limit on mount.
 * Exposes a `loadMore` callback that re-fetches with the server's returned
 * `nextCursor` and appends the new entries to the local accumulator so the
 * Recently_Updated_Page's "Load more" affordance renders a growing list
 * (design.md § Data Flows > "Load more" click).
 */
export const useRecentlyUpdatedList = (
    scope: RecentlyUpdatedScope,
    limit: number
): UseRecentlyUpdatedListResult => {
    // `throwOnError: true` so a server failure rejects the Promise rather
    // than only setting the shared `useApi.error` state. The hook catches
    // the rejection, logs with structured context and surfaces it as a
    // plain `error` field — nothing throws up to the component tree
    // (requirement 7.4).
    const { makeRequest } = useApi({ method: 'GET' }, true);

    const [data, setData] = useState<RecentlyUpdatedEntry[] | undefined>();
    const [nextCursor, setNextCursor] = useState<string | null | undefined>();
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<Error | AxiosError | undefined>();
    const cursorRef = useRef<string | null | undefined>(undefined);

    const buildUrl = useCallback(
        (cursor?: string | null) => {
            const params = new URLSearchParams();
            params.set('scope', scope);
            params.set('limit', String(limit));
            if (cursor) {
                params.set('cursor', cursor);
            }
            return `${LIST_ENDPOINT}?${params.toString()}`;
        },
        [scope, limit]
    );

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setError(undefined);
        Promise.resolve(makeRequest({ url: buildUrl() }))
            .then((response) => {
                if (cancelled) return;
                const body = response?.data as
                    | RecentlyUpdatedResponse
                    | undefined;
                setData(body?.items ?? []);
                const cur = body?.nextCursor ?? null;
                setNextCursor(cur);
                cursorRef.current = cur;
            })
            .catch((err: Error | AxiosError) => {
                if (cancelled) return;
                console.error('[RecentlyUpdated] list fetch failed', {
                    scope,
                    limit,
                    error: err,
                });
                setData(undefined);
                setError(err);
            })
            .finally(() => {
                if (cancelled) return;
                setLoading(false);
            });
        return () => {
            cancelled = true;
        };
        // Re-fire the initial fetch when the inputs change.
    }, [scope, limit, buildUrl]);

    const loadMore = useCallback(async () => {
        const cursor = cursorRef.current;
        if (!cursor) return;
        setLoading(true);
        try {
            const response = await makeRequest({
                url: buildUrl(cursor),
            });
            const body = response?.data as RecentlyUpdatedResponse | undefined;
            setData((prev) => [...(prev ?? []), ...(body?.items ?? [])]);
            const cur = body?.nextCursor ?? null;
            setNextCursor(cur);
            cursorRef.current = cur;
        } catch (err) {
            console.error('[RecentlyUpdated] loadMore failed', {
                scope,
                limit,
                cursor,
                error: err,
            });
            setError(err as Error | AxiosError);
        } finally {
            setLoading(false);
        }
    }, [scope, limit, buildUrl, makeRequest]);

    return { data, nextCursor, loading, error, loadMore };
};

/**
 * Reads the per-user Updates_Read_State on mount and exposes a `markRead`
 * callback that POSTs the mark-read endpoint. The Recently_Updated_Bell
 * only reads `lastViewedAt`; the Recently_Updated_Page invokes `markRead`
 * on mount — this hook does NOT auto-fire mark-read (requirement 4.6).
 */
export const useUpdatesReadState = (): UseUpdatesReadStateResult => {
    const { makeRequest: makeGetRequest } = useApi(
        { url: READ_STATE_ENDPOINT, method: 'GET' },
        true
    );
    const { makeRequest: makeMarkReadRequest } = useApi(
        { url: MARK_READ_ENDPOINT, method: 'POST' },
        true
    );

    const [lastViewedAt, setLastViewedAt] = useState<number | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<Error | AxiosError | undefined>();

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setError(undefined);
        Promise.resolve(makeGetRequest())
            .then((response) => {
                if (cancelled) return;
                const body = response?.data as
                    | UpdatesReadStateResponse
                    | undefined;
                setLastViewedAt(body?.lastViewedAt ?? null);
            })
            .catch((err: Error | AxiosError) => {
                if (cancelled) return;
                console.error('[RecentlyUpdated] read-state fetch failed', {
                    error: err,
                });
                setLastViewedAt(null);
                setError(err);
            })
            .finally(() => {
                if (cancelled) return;
                setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, []);

    const markRead = useCallback(async () => {
        try {
            const response = await makeMarkReadRequest();
            const body = response?.data as UpdatesReadStateResponse | undefined;
            if (body?.lastViewedAt != null) {
                setLastViewedAt(body.lastViewedAt);
            }
        } catch (err) {
            console.error('[RecentlyUpdated] mark-read failed', {
                error: err,
            });
            // Swallow — mark-read is fire-and-forget from the user's
            // perspective (requirement 5.7).
        }
    }, [makeMarkReadRequest]);

    return { lastViewedAt, loading, error, markRead };
};
